import { Matcher } from 'react-day-picker';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { mergeClasses } from '@/utils';
import { formatDate, getDate } from '@/helpers';

import { CalendarIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@/assets/icons';
import {
  Button,
  Calendar,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SvgIcon,
} from '@/components/common';

import { DEFAULT_MAX_YEAR, DEFAULT_MIN_YEAR } from './DateRangePicker.constants';

interface IDateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface IDateRangePickerRef {
  onClear: () => void;
}

interface IDateRangePicker {
  /** Click handler for applying the updates from DateRangePicker. */
  onUpdate?: (values: { range: IDateRange }) => void;
  /** Initial value for start date */
  initialDateFrom?: Date | string;
  /** Initial value for end date */
  initialDateTo?: Date | string;
  /** Alignment of popover */
  align?: 'start' | 'center' | 'end';
  /** Option for locale */
  locale?: string;
  /** Minimum year for the calendar */
  minYear?: number;
  /** Maximum year for the calendar */
  maxYear?: number;
  /** Additional class name for the date range picker */
  className?: string;
  /** Label for the date range picker */
  label?: string;
  /** Is the date range picker required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Is the date range picker disabled */
  disabled?: boolean;
  /** Prefix icon for the date range picker */
  prefixIcon?: React.ReactNode;
  /** Check if suffix icon should be displayed */
  hasSuffixIcon?: boolean;
  /** Additional class name for the apply button */
  buttonClassName?: string;
  /** Placeholder text for the date range picker */
  placeholder?: string;
  /** Allow clearing the selected date range */
  allowClear?: boolean;
  /** Disable specific dates in the calendar */
  calendarDisabled?: Matcher | Matcher[] | undefined;
  /** Render the popover as modal */
  isModal?: boolean;
}

/** The DateRangePicker component allows a user to select a range of dates */
const DateRangePicker = forwardRef<IDateRangePickerRef, IDateRangePicker>(
  (
    {
      initialDateFrom,
      initialDateTo,
      onUpdate,
      align = 'end',
      locale = 'en-GB',
      minYear = DEFAULT_MIN_YEAR,
      maxYear = DEFAULT_MAX_YEAR,
      className,
      label,
      required,
      error,
      disabled,
      buttonClassName,
      placeholder,
      allowClear,
      calendarDisabled,
      isModal = false,
      hasSuffixIcon = false,
    }: IDateRangePicker,
    ref,
  ) => {
    const [isOpen, setOpen] = useState(false);

    const [range, setRange] = useState<IDateRange>({
      from: getDate(initialDateFrom),
      to: getDate(initialDateTo),
    });

    // To prevent onUpdate from firing on initial render
    const isInitialRender = useRef(true);

    useEffect(() => {
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }
      onUpdate?.({ range });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    const onClear = (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      setRange({ from: undefined, to: undefined });
      onUpdate?.({ range });
    };

    useImperativeHandle(ref, () => ({
      onClear: onClear,
    }));

    return (
      <div className={mergeClasses('flex flex-col gap-2', className)}>
        {label && (
          <Label className="!gap-0 font-semibold text-body-sm text-neutral-9">
            {label}
            {required && <span className="ml-1 text-semantic-error-base">*</span>}
          </Label>
        )}
        <Popover modal={isModal} open={isOpen} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              buttonStyle="outline"
              buttonColor="neutral"
              borderRadius="soft"
              className={mergeClasses(
                'focus:shadow-none border-neutral-4 !justify-start',
                // Disable state
                disabled && '[&_*]:text-neutral-7',
                // Data state: open
                'data-[state=open]:border-primary-base',
                error && 'border-semantic-error-base',
                buttonClassName,
              )}
              prefixIcon={
                <CalendarIcon
                  className={mergeClasses(
                    'size-5 text-primary-base',
                    disabled && '!text-neutral-6',
                  )}
                />
              }
              suffixIcon={
                (hasSuffixIcon || allowClear) && (
                  <div className="flex items-center gap-1">
                    {allowClear && (range.from || range.to) && (
                      <CloseIcon
                        className="!size-5 cursor-pointer text-neutral-6 dark:text-neutral-6 hover:text-neutral-10 dark:hover:text-neutral-5"
                        onClick={() => onClear()}
                      />
                    )}
                    {hasSuffixIcon && (
                      <SvgIcon
                        className={mergeClasses(
                          'size-5 cursor-pointer text-neutral-10 dark:text-primary-base',
                          isOpen && '!text-primary-base',
                        )}
                        component={isOpen ? ChevronUpIcon : ChevronDownIcon}
                      />
                    )}
                  </div>
                )
              }
              disabled={disabled}
            >
              <div
                className={mergeClasses(
                  'font-medium text-body-sm w-full flex-1 text-left',
                  !range.from || !range.to
                    ? 'text-neutral-7'
                    : 'text-neutral-10 dark:!text-neutral-0',
                )}
              >
                {placeholder && !range.from && !range.to
                  ? placeholder
                  : `${formatDate({
                      date: range.from,
                      defaultText: 'Start Date',
                      locale,
                    })} - ${formatDate({
                      date: range.to,
                      defaultText: 'End Date',
                      locale,
                    })}`}
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align={align}
            side="bottom"
            avoidCollisions={false}
            className="p-none !w-[300px] bg-neutral-0"
          >
            <Calendar
              mode="range"
              onSelect={(value: { from?: Date; to?: Date } | undefined) => {
                if (!value?.from || !value?.to) return;
                // Start a new range selection if 'from' date is earlier than current 'from' date or both dates are set
                // The selected date is the one that is not equal to the current 'from' date
                const selectedDate = range.from === value.from ? value.to : value.from;
                if (!range.from || range.from > selectedDate || (range.from && range.to)) {
                  setRange({ from: selectedDate, to: undefined });
                  return;
                }
                const to = value.to;
                to.setHours(23, 59, 59, 999); // Set to end of day
                setRange({ from: value.from, to: to });
                setOpen(false);
              }}
              selected={range}
              numberOfMonths={1}
              defaultMonth={new Date()}
              startMonth={new Date(minYear, 0)}
              endMonth={new Date(maxYear, 11)}
              captionLayout="dropdown"
              footer
              disabled={calendarDisabled}
            />
          </PopoverContent>
        </Popover>
        {error && <p className="text-xs text-semantic-error-base">{error}</p>}
      </div>
    );
  },
);

DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker, type IDateRange, type IDateRangePicker };
